import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Arr } from "../../components/CTA";

/* ───────────────────────── Interactive calculator ─────────────────────── */

function Calculator() {
  const [docs, setDocs] = useState(1200);
  const [entryMin, setEntryMin] = useState(4);
  const [mixedPct, setMixedPct] = useState(40);
  const [presortMin, setPresortMin] = useState(4);

  const data = useMemo(() => {
    const entry = docs * entryMin;
    const presort = docs * (mixedPct / 100) * presortMin;
    const total = entry + presort;
    const entryPct = total > 0 ? (entry / total) * 100 : 0;
    const presortPct = total > 0 ? Math.round((presort / total) * 100) : 0;
    return {
      entryMin: entry,
      presortMin: presort,
      totalMin: total,
      entryHours: Math.round(entry / 60),
      presortHours: Math.round(presort / 60),
      totalHours: Math.round(total / 60),
      entryPct,
      presortPct,
    };
  }, [docs, entryMin, mixedPct, presortMin]);

  return (
    <aside className="calc" aria-labelledby="calc-title" data-testid="bottleneck-calculator">
      <header className="calc__head">
        <h3 id="calc-title" className="calc__title">Where does your document time actually go?</h3>
        <p className="calc__intro">
          Set the four inputs to match your own operation. Nothing is sent anywhere, the maths runs in your browser.
        </p>
      </header>

      <div className="calc__inputs">
        <Slider
          id="docs"
          label="Documents processed per month"
          min={100} max={10000} step={100}
          value={docs}
          onChange={setDocs}
          format={(v) => v.toLocaleString()}
          testid="slider-docs"
        />
        <Slider
          id="entry"
          label="Minutes of manual data entry per document"
          min={1} max={20} step={0.5}
          value={entryMin}
          onChange={setEntryMin}
          format={(v) => `${v} min`}
          testid="slider-entry"
        />
        <Slider
          id="mixed"
          label="Share of files that arrive mixed or batched"
          min={0} max={100} step={5}
          value={mixedPct}
          onChange={setMixedPct}
          format={(v) => `${v}%`}
          testid="slider-mixed"
        />
        <Slider
          id="presort"
          label="Minutes to sort and identify each of those, before processing"
          min={0.5} max={12} step={0.5}
          value={presortMin}
          onChange={setPresortMin}
          format={(v) => `${v} min`}
          testid="slider-presort"
        />
      </div>

      <div className="calc__bar" role="img" aria-label={`Manual entry ${data.entryHours} hours, pre-sort ${data.presortHours} hours per month`}>
        <div
          className="calc__seg calc__seg--entry"
          style={{ width: `${data.entryPct}%` }}
          data-testid="bar-entry"
        >
          <div className="calc__seg-inner">
            <b>Manual entry</b>
            <span>the visible bottleneck</span>
            <em>{data.entryHours} hrs / month</em>
          </div>
        </div>
        <div
          className="calc__seg calc__seg--presort"
          style={{ width: `${100 - data.entryPct}%` }}
          data-testid="bar-presort"
        >
          <div className="calc__seg-inner">
            <b>Pre-sort and classification</b>
            <span>the invisible bottleneck</span>
            <em>{data.presortHours} hrs / month</em>
          </div>
        </div>
      </div>

      <p className="calc__headline" data-testid="calc-headline">
        The pre-sort step is <span className="calc__num">{data.presortPct}%</span> of your document time.
      </p>
      <p className="calc__support">
        That is roughly {data.presortHours} hours every month, work that rarely lands on a timesheet.
      </p>
      <p className="calc__total">
        Total document handling time: about {data.totalHours} hours a month.
      </p>
      <p className="calc__foot">
        These are your numbers, not ours. The exact figure is not the point. The point is that the second slice is real, and almost nobody measures it.
      </p>
    </aside>
  );
}

function Slider({ id, label, min, max, step, value, onChange, format, testid }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="calc__row">
      <label htmlFor={id} className="calc__label">{label}</label>
      <div className="calc__field">
        <input
          id={id}
          type="range"
          min={min} max={max} step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="calc__range"
          style={{ "--p": `${pct}%` }}
          data-testid={testid}
          aria-valuetext={format(value)}
        />
        <output className="calc__output" htmlFor={id}>{format(value)}</output>
      </div>
    </div>
  );
}

/* ────────────────────────────── Page ────────────────────────────── */

export default function TwoBottlenecks() {
  return (
    <article className="article">
      {/* Header */}
      <header className="article__head">
        <div className="container container--narrow">
          <nav className="crumb" aria-label="Breadcrumb">
            <Link to="/">Docket</Link> / <Link to="/insights">Insights</Link> / The two bottlenecks every document team has
          </nav>
          <p className="article__eyebrow">Field notes &nbsp;·&nbsp; January 2026 &nbsp;·&nbsp; 6 min read</p>
          <h1 className="article__title">The two bottlenecks every document team has, and why one is invisible</h1>
          <p className="article__standfirst">
            Manual entry gets all the attention. The pre-sort step quietly eats just as much time, and almost nobody measures it.
          </p>
          <hr className="article__rule" />
        </div>
      </header>

      {/* Body */}
      <div className="container container--narrow article__body">

        <p>
          Ask anyone who runs a document-heavy operation where the bottleneck is, and you tend to get the same answer: manual entry. People at a screen, reading an invoice or a claim form or a contract, typing what they see into another system. It is slow, it is expensive, and everyone knows it. It sits near the top of every automation wishlist.
        </p>
        <p>
          They are not wrong. Manual entry is a real bottleneck. But it is not the only one, and the second is much harder to see, because almost nothing about the way teams work is set up to notice it.
        </p>

        <h2 className="article__sub">The bottleneck everyone can see</h2>
        <p>
          Manual entry gets the attention for good reasons. It is concentrated: you can walk past a desk and watch it happen. It is measurable: documents per hour, jobs per day, a queue that visibly grows or shrinks. And it has an owner: there is a person, or a team, whose job this plainly is.
        </p>
        <p>
          When something is concentrated, measurable and owned, it gets noticed. It gets resented. Eventually it gets a budget. So manual entry is the bottleneck that makes it into the business case. Fair enough.
        </p>

        <h2 className="article__sub">The bottleneck nobody measures</h2>
        <p>
          Before a single field can be keyed, something else has to happen. Someone has to work out what each document actually is.
        </p>
        <p>
          A scanned batch lands in a shared inbox. It is eleven pages long, and it is not one document. It is an invoice, then a purchase order, then a two-page covering letter. An email arrives with one PDF attached, and the PDF is three separate claims that someone stapled together upstream. A supplier sends a statement that turns out to be a statement, two invoices and a credit note.
        </p>
        <p>
          Before any of that can be processed, a person has to open it, find where one document ends and the next begins, identify each piece, and route it to the right place. Call it the pre-sort. Call it triage, or pre-classification. Most teams do not call it anything at all, which is exactly the problem.
        </p>
        <p>Here is why it stays invisible:</p>

        <ul className="article__list">
          <li><strong>It is distributed.</strong> It does not happen at one desk. It is spread across whoever opens the mail, whoever uploads the batch, whoever notices halfway through that this is not the document they thought it was. No single person carries the full weight of it.</li>
          <li><strong>It happens before the part you measure.</strong> Throughput metrics start counting once a document is in the queue. The sorting happened upstream of the queue, so it happened upstream of the measurement. As far as the numbers are concerned, it never happened.</li>
          <li><strong>It is nobody's job.</strong> It is not a role, and it is not a step on the process map. It is thirty seconds here, two minutes there, a quiet decision to just split this PDF before passing it on. It never reaches a timesheet.</li>
          <li><strong>It feels like setup, not work.</strong> And setup does not get measured. It gets tolerated.</li>
        </ul>

        <h2 className="article__sub">Why this matters</h2>
        <p>
          A bottleneck you do not measure is a bottleneck you cannot fix. You cannot put it in a business case, you cannot tell whether it is getting better or worse, and you cannot make a sound decision about automation, because you are judging tools against only half of the problem.
        </p>
        <p>
          That last point is the one that costs real money. A good deal of document automation handles the keying and quietly leaves the sorting to you. If the pre-sort is a meaningful share of your team's time, and you buy a tool that ignores it, you have automated the visible half of the problem and kept the invisible half. The work does not disappear. It just stays invisible, now with a software invoice next to it.
        </p>

        <h2 className="article__sub">See it for yourself</h2>
        <p>
          The honest way to settle this is not to borrow anyone's benchmark. It is to put in your own numbers.
        </p>

        <Calculator />

        <p>
          Move the inputs until they match your own operation. The split you get is yours, not ours. The exact percentage is not the point. The point is that the second slice is almost always larger than people expect, and almost nobody was measuring it.
        </p>

        <h2 className="article__sub">What to do about it</h2>
        <p>Two things, and neither one requires buying anything.</p>
        <ol className="article__list article__list--num">
          <li>
            <strong>Measure it.</strong> Instrument one ordinary week. Ask the people who open and upload documents to track, roughly, the time they spend working out what things are and separating them. Rough is fine. You are not chasing a precise figure, you are after the order of magnitude, and the order of magnitude is usually enough to change the conversation.
          </li>
          <li>
            <strong>Judge automation against the whole problem.</strong> When you evaluate a tool, do not only ask how accurately it extracts. Ask what happens when a single file holds three different documents. If the answer is that a person separates those first, then the tool automates the bottleneck you could already see and leaves the one you could not.
          </li>
        </ol>

        <h2 className="article__sub">The short version</h2>
        <p>
          The pre-sort step is invisible because it is boring and distributed, not because it is small. Make it visible, and you often find that the fastest automation win available to you is the one nobody at the table was talking about.
        </p>

        {/* Closing CTA */}
        <section className="article__cta" data-testid="article-cta">
          <div>
            <h3>See the invisible bottleneck on your own documents.</h3>
            <p>A Docket pilot runs splitting, classification and extraction on two or three of your real document types, in four to six weeks.</p>
          </div>
          <Link to="/contact" className="article__cta-btn" data-testid="article-cta-btn">
            Request a pilot <Arr />
          </Link>
        </section>

        {/* Back link */}
        <div className="article__backwrap">
          <Link to="/insights" className="txtlink" data-testid="article-back">
            <svg className="arr" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ transform: "rotate(180deg)" }}>
              <path d="M3 8h9m0 0L8 4m4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to all Insights
          </Link>
        </div>
      </div>
    </article>
  );
}
